<template>
  <div class="container mx-auto px-4 py-12">
    <MainTitle
      el="h1"
      class="text-primary-A900 font-body text-center mb-5 text-1.6xl lg:text-2xl-1 font-black"
      align="left"
    >
      <template #before>
        {{ $t('miracoCulture') }}
      </template>
      {{ $t('events') }}
    </MainTitle>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      <template v-if="isFetching">
        <div v-for="n in 9" :key="n" class="rounded-2xl shadow-custom-5 p-6 flex flex-col h-full shimmer-card">
          <div class="w-full h-40 bg-gray-300 rounded-xl mb-6 loading"></div>
          <div class="flex items-center gap-2 mb-1">
            <div class="w-4 h-4 bg-gray-300 rounded-full loading"></div>
            <div class="w-24 h-4 bg-gray-300 rounded loading"></div>
          </div>
          <div class="w-full h-6 bg-gray-300 rounded mb-4 mt-4 loading"></div>
          <div class="flex items-center gap-2 mb-4">
            <div class="w-4 h-4 bg-gray-300 rounded-full loading"></div>
            <div class="w-32 h-4 bg-gray-300 rounded loading"></div>
          </div>
          <div class="w-full h-10 bg-gray-300 rounded mb-6 loading"></div>
          <div class="w-28 h-4 bg-gray-300 rounded mt-auto loading"></div>
        </div>
      </template>
      <AppLink
        v-for="event in events"
        :key="event?.id"
        :to="`/events/${event?.id}`"
        class="rounded-2xl shadow-custom-5 p-6 flex flex-col h-full transition-transform hover:scale-[1.02] cursor-pointer"
      >
        <img v-if="event?.image_url" :src="event.image_url" alt="" class="w-full h-40 object-cover rounded-xl mb-6" />
        <div class="flex items-center gap-2 text-primary-A1100 text-tiny mb-1">
          <svg-icon name="calendar" class="w-4 h-4 mr-1" />
          <span>{{ event?.date }}</span>
        </div>
        <h2 class="text-xl font-bold text-primary-A1100 mb-4 mt-4">
          {{ event?.title }}
        </h2>
        <div class="flex items-center gap-2 text-primary-A1100 text-tiny mb-4">
          <svg-icon name="loc" class="w-4 h-4 mr-1" />
          <span>{{ event?.location }}</span>
        </div>
        <div class="text-secondary-700 text-tiny mb-6 line-clamp-2">
          {{ event?.short_description }}
        </div>
        <div class="text-primary-800 underline text-tiny flex items-center gap-1 hover:underline mt-auto">
          {{ $t('viewDetails') }}
          <svg-icon name="chveron-right" class="w-3 h-3 ml-1" />
        </div>
      </AppLink>
    </div>

    <div class="flex flex-col items-center justify-center mt-12 gap-4">
      <div class="text-gray-500 text-sm">
        {{ $t('showing') }} {{ events.length }} {{ $t('outOf') }} {{ totalEvents }}
      </div>
      <button
        v-if="events.length < totalEvents"
        class="px-8 py-3 bg-white text-primary-800 rounded-full font-bold border-1 border-primary-800 transition-all"
        @click="loadMore"
      >
        {{ $t('loadMore') }}
      </button>
      <a href="#" class="text-primary-700 text-tiny underline mt-2">{{ $t('backToTop') }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
const { t: $t } = useI18n({ useScope: 'local' });
const pageSize = 8;
const currentPage = ref(1);

const variables = computed(() => ({
  pageSize,
  currentPage: currentPage.value,
}));

const { events, totalEvents, isFetching } = useAppEvents(variables);

function loadMore() {
  currentPage.value++;
}
</script>

<i18n>
{
  "en": {
    "miracoCulture": "Miraco Culture &",
    "events": "Events",
    "viewDetails": "View Details",
    "showing": "Showing",
    "outOf": "out of",
    "loadMore": "Load More Events",
    "backToTop": "Back To Top"
  },
  "ar": {
    "miracoCulture": "ثقافة ميراكو و",
    "events": "الفعاليات",
    "viewDetails": "عرض التفاصيل",
    "showing": "عرض",
    "outOf": "من أصل",
    "loadMore": "تحميل المزيد من الفعاليات",
    "backToTop": "العودة للأعلى"
  }
}
</i18n>
